<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { mdiClose } from '@quasar/extras/mdi-v7';
import type { AnalyticsSummary } from 'src/modules/Analytics/types';
import StreakCard from 'src/modules/Analytics/components/StreakCard/StreakCard.vue';
import StatSummaryCard from 'src/modules/Analytics/components/StatSummaryCard/StatSummaryCard.vue';
import AverageScoreCard from 'src/modules/Analytics/components/AverageScoreCard/AverageScoreCard.vue';
import TotalTimeCard from 'src/modules/Analytics/components/TotalTimeCard/TotalTimeCard.vue';
import CategoryBreakdownCard from 'src/modules/Analytics/components/CategoryBreakdownCard/CategoryBreakdownCard.vue';
import RecentActivityList from 'src/modules/Analytics/components/RecentActivityList/RecentActivityList.vue';

const props = defineProps<{
  modelValue: boolean;
  studentName: string;
  summary: AnalyticsSummary | null;
  isLoading?: boolean;
  error?: string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const { t } = useI18n();

function formatScore(value: number | null | undefined): string {
  return typeof value === 'number' ? `${Math.round(value)}%` : '-';
}

const lastActivityLabel = computed(() => {
  const days = props.summary?.timeSinceLastActivity;
  if (typeof days !== 'number') return '-';
  if (days <= 0) return t('TODAY');
  return t('DAYS_AGO', { count: days });
});

const insights = computed(() => [
  { label: t('HELP_USAGE'), value: `${props.summary?.helpUsage ?? 0}` },
  { label: t('FIRST_ATTEMPT_SCORE'), value: formatScore(props.summary?.firstAttemptScore) },
  { label: t('FINAL_SCORE'), value: formatScore(props.summary?.finalScore) },
  { label: t('LAST_PRACTICE'), value: lastActivityLabel.value },
]);
</script>

<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <q-card class="student-stats-dialog">
      <q-card-section class="row items-center q-pb-sm">
        <div class="column">
          <div class="stat-card__title">{{ $t('STUDENT_STATISTICS') }}</div>
          <div class="text-h6 text-weight-bold">{{ studentName || '-' }}</div>
        </div>
        <q-space />
        <q-btn
          dense
          flat
          round
          :icon="mdiClose"
          @click="emit('update:modelValue', false)"
        />
      </q-card-section>

      <q-separator />

      <q-card-section class="stats-body">
        <q-inner-loading :showing="!!isLoading">
          <q-spinner-gears
            size="40px"
            color="primary"
          />
        </q-inner-loading>

        <div
          v-if="error"
          class="text-negative q-mb-md"
        >
          {{ error }}
        </div>

        <template v-if="summary && !isLoading">
          <div class="row q-col-gutter-md q-mb-md">
            <div class="col-12 col-sm-6 col-md-3">
              <StreakCard
                :streak="summary.streak"
                :longest-streak="summary.longestStreak"
              />
            </div>
            <div class="col-12 col-sm-6 col-md-3">
              <StatSummaryCard
                :completed="summary.completed || { steps: 0, sections: 0, courses: 0 }"
                :incomplete="summary.incomplete || { steps: 0, sections: 0, courses: 0 }"
              />
            </div>
            <div class="col-12 col-sm-6 col-md-3">
              <AverageScoreCard :average-score="summary.averageScore" />
            </div>
            <div class="col-12 col-sm-6 col-md-3">
              <TotalTimeCard :total-time-ms="summary.totalTimeMs" />
            </div>
          </div>

          <div class="stat-card q-mb-md">
            <div class="row q-col-gutter-md">
              <div
                v-for="insight in insights"
                :key="insight.label"
                class="col-6 col-sm-3 text-center"
              >
                <div class="stat-value">{{ insight.value }}</div>
                <div class="stat-sub q-mt-xs">{{ insight.label }}</div>
              </div>
            </div>
          </div>

          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-6">
              <CategoryBreakdownCard :category-breakdown="summary.categoryBreakdown" />
            </div>
            <div class="col-12 col-md-6">
              <RecentActivityList :recent-activity="summary.recentActivity" />
            </div>
          </div>
        </template>

        <div
          v-else-if="!isLoading && !error"
          class="stat-empty"
        >
          {{ $t('NO_DATA_AVAILIABLE') }}
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.student-stats-dialog {
  width: 900px;
  max-width: 96vw;
  border-radius: 16px;
}
.stats-body {
  position: relative;
  min-height: 160px;
  background: #f7f8fa;
}
.body--dark .stats-body {
  background: #161616;
}
</style>
